import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface HelpHintProps {
  /** Tooltip body — short string or JSX for multi-line help. */
  text: React.ReactNode;
  /** Small (default) suits inline use next to a field label; medium for headings. */
  size?: 'small' | 'medium';
  /** Override default placement; MUI tooltip placement string. */
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'left';
}

/**
 * Inline hover-help marker. Renders a subtle ? icon that shows a
 * tooltip on hover. Use to expose explanatory copy for admin fields,
 * buttons, and table headers without bloating the visible UI.
 *
 * Pair with field labels and section headings — not full sentences in
 * a paragraph (use plain text for those instead).
 */
export const HelpHint: React.FC<HelpHintProps> = ({
  text,
  size = 'small',
  placement = 'top',
}) => (
  <Tooltip title={text} placement={placement} arrow enterDelay={200}>
    <IconButton
      size={size}
      aria-label="help"
      sx={{
        p: 0.25,
        ml: 0.5,
        color: 'text.secondary',
        '&:hover': { color: 'primary.main' },
      }}
    >
      <HelpOutlineIcon fontSize="inherit" sx={{ fontSize: size === 'small' ? 16 : 20 }} />
    </IconButton>
  </Tooltip>
);
